/**
 * SINGLE SOURCE OF TRUTH for all graph visual tokens.
 *
 * Rules:
 *  - Every node type has exactly one color and one shape — consistent across ALL views.
 *  - Every edge type has exactly one color — consistent across ALL views.
 *  - No two colors in NODE_COLORS or EDGE_COLORS should be perceptually similar
 *    to a human on a white background (no two greens, no two reds, etc.).
 *  - Do NOT hardcode any of these values elsewhere in the frontend.
 *
 * Color families used (each family used at most twice — once for a node, once for an edge,
 * at clearly different luminance/saturation levels):
 *
 *  Palette redesigned 2026-05-30 for maximal perceptual separation: every pair
 *  in NODE_COLORS is >= 15 CIE2000 dE apart, every pair in EDGE_COLORS >= 13
 *  CIE2000 (tokens.contrast.test.js, with a short sibling-exemption list) AND
 *  >= 18 CIE76 (tests/smoke/test_graph_visual_per_type.py) — two metrics, both
 *  enforced,
 *  with NO "shades of the same hue" (the old palette had 4 near-identical
 *  purples and several orange/dark pairs at dE 3-13). Enforced by the
 *  regression test tests/test_palette_contrast (frontend) — see that file.
 *  The few intentional sibling pairs that sit near 13-16 dE are also
 *  disambiguated by edge dash pattern + width + opacity:
 *    - USES_COMPONENT vs USES_TECHNIQUE (both "uses X" relations)
 *    - COMPETES_WITH  vs CONTRADICTS    (both "conflict" relations, rarely co-occur)
 *    - FORECAST       vs CONTRADICTS    (FORECAST is dashedLong + reduced opacity)
 *
 *  Nodes  : cobalt-blue, violet, red, orange, yellow, forest-green, cyan,
 *            teal, olive, magenta-rose, blue-grey, brown, indigo, light-grey.
 *  Edges  : medium-blue, purple, teal, cyan, indigo, orange, deep-orange,
 *            yellow, lime, green, dark-green, pink, red, magenta, black,
 *            slate, light-grey, brown.
 */

// ---------------------------------------------------------------------------
// Node colors — filled shapes (circles, diamonds, stars, etc.)
// ---------------------------------------------------------------------------
export const NODE_COLORS = {
  method:        '#1565C0',  // cobalt blue   (named load-bearing entity)
  author:        '#7B1FA2',  // violet-purple (people)
  breakthrough:  '#E53935',  // red           (disruptive — diamond/dominant)
  innovative:    '#FB8C00',  // orange        (strong but not breakthrough)
  task:          '#FDD835',  // yellow        (goals / benchmark tasks)
  consolidating: '#2E7D32',  // forest green
  survey:        '#26C6DA',  // cyan          (review / survey paper)
  component:     '#00897B',  // teal          (parts — distinct from cyan/green)
  technique:     '#8BC34A',  // leaf green    (method-adjacent; the old olive read as mud)
  problem:       '#D81B60',  // magenta-rose  (distinct from red breakthrough)
  reference:     '#607D8B',  // blue-grey     (bibliography stub)
  claim:         '#795548',  // brown         (assertions / evidence text)
  dataset:       '#A38B29',  // ochre         (benchmarks; the old navy read as the method blue)
  concept:       '#82B1FF',  // light cobalt  (a method named in papers, no paper of its own here)
  // A paper's stated future direction: context, not an entity the paper made.
  // Muted orchid, 23.0 CIE2000 dE from its nearest node colour (problem).
  future_topic:  '#BB77BB',  // orchid        (future direction)
  // A survey's own theme (its taxonomy / categories), grouping what it reviews.
  // Dark olive, 21.1 CIE2000 dE from its nearest node colour (claim).
  theme:         '#665500',  // dark olive    (survey theme)
  other:         '#CFD8DC',  // light grey    (unknown / low-salience, off-white)
};

// ---------------------------------------------------------------------------
// Node shapes
// ---------------------------------------------------------------------------
export const NODE_SHAPES = {
  method:        'square',
  survey:        'diamond',
  breakthrough:  'diamond',
  consolidating: 'diamond',
  innovative:    'diamond',
  author:        'square',
  task:          'triangle',
  problem:       'diamond',
  component:     'triangle',
  technique:     'triangle',
  reference:     'diamond',
  claim:         'triangle',
  dataset:       'square',
  concept:       'border',
  future_topic:  'border',
  theme:         'border',
  other:         'border',
};

// ---------------------------------------------------------------------------
// Human-readable labels
// ---------------------------------------------------------------------------
export const NODE_TYPE_LABELS = {
  method:        'Method',
  breakthrough:  'Breakthrough',
  consolidating: 'Consolidating',
  innovative:    'Innovative',
  survey:        'Survey / Review',
  author:        'Author',
  task:          'Task',
  problem:       'Problem',
  component:     'Component',
  technique:     'Technique',
  reference:     'Reference',
  claim:         'Claim',
  dataset:       'Dataset',
  concept:       'Named method',
  future_topic:  'Future direction',
  theme:         'Survey theme',
  other:         'Other',
};

export const NODE_SHAPE_LABELS = {
  circle:      'Circle',
  square:      'Square',
  triangle:    'Triangle',
  invtriangle: 'Inv. Triangle',
  diamond:     'Diamond',
  hexagon:     'Hexagon',
  pentagon:    'Pentagon',
  star:        'Star',
  border:      'Circle',
};

// ---------------------------------------------------------------------------
// Edge colors — thin directed lines
// Each edge type gets a DISTINCT color, different family from each other.
// Where an edge shares a hue family with a node, it is clearly lighter / more vivid.
// ---------------------------------------------------------------------------
export const EDGE_COLORS = {
  EXTENDS:        '#1E88E5',  // medium blue   (lighter than method cobalt)
  CREATED:        '#8E24AA',  // purple        (authorship — only purple in palette)
  CO_AUTHORED:    '#66BB6A',  // green         (co-author collaboration — authors theme)
  USES_COMPONENT: '#00897B',  // teal          ("uses a part")
  USES_TECHNIQUE: '#00BCD4',  // cyan          ("uses a technique" — sibling of above)
  SOLVES:         '#3949AB',  // indigo        (solves a problem)
  CITES:          '#FB8C00',  // orange        (bibliographic)
  FORECAST:       '#F4511E',  // deep-orange   (prediction — also dashedLong + low opacity)
  ACHIEVES:       '#FFD600',  // bright yellow (distinct from orange CITES)
  COMBINES:       '#AEEA00',  // lime          (structural merge)
  // Was '#43A047' green, which sat 11.8 CIE dE from CO_AUTHORED '#66BB6A' — the
  // palette had THREE greens in a row (co-authored / consolidates / supports),
  // the exact "shades of the same hue" this file forbids, and two edge types read
  // as one colour in the legend. Olive is 38.9 dE from its nearest neighbour.
  CONSOLIDATES:   '#9E9D24',  // olive         (consolidating work)
  SUPPORTS:       '#1B5E20',  // dark green    (evidence support)
  COMPETES_WITH:  '#EC407A',  // pink          (competition)
  CONTRADICTS:    '#FF1744',  // red           (evidence conflict)
  MAKES_CLAIM:    '#AD1457',  // magenta       (method -> claim)
  DISRUPTS:       '#000000',  // black         (field-shift links)
  EVALUATED_ON:   '#546E7A',  // slate         (benchmark evaluation)
  REFERENCES:     '#B0BEC5',  // light grey    (weak bibliographic)
  APPLIED_TO:     '#795548',  // brown         (application target)
  // Component<->Component PMI synergy. Salmon, because this edge is only ever
  // drawn in the `synergy` view alongside COMBINES lime, USES_TECHNIQUE cyan,
  // USES_COMPONENT teal and EXTENDS blue — a warm tone is 43.5 CIE2000 dE from
  // the nearest of those, and 23.8 dE from its nearest neighbour palette-wide
  // (CITES orange), so it clears the 13 dE contract with room.
  TECHNIQUE_SYNERGY: '#FFB3A6',  // salmon    (component synergy)
  // Survey -> a method it reviews. Muted lavender-grey and thin: context, not a
  // relation between methods. 22.6 CIE2000 dE from its nearest (REFERENCES).
  COVERS:         '#9988AA',  // lavender-grey (survey coverage)
  // What a survey reviews besides methods, the scores it quotes, papers' future
  // directions and problem-problem relatedness (audit 2026-09-26). Chosen by a
  // CIE2000 search against the whole palette: the closest pair any of them forms
  // is 19.4 dE (REPORTED_RESULT / CO_AUTHORED), all distinct from COVERS.
  REVIEWS_DATASET:  '#886600',  // dark ochre  (survey reviews a benchmark/dataset)
  REVIEWS_TASK:     '#DDCCAA',  // sand        (survey reviews a task)
  REPORTED_RESULT:  '#11EECC',  // mint        (a score quoted by a survey)
  FUTURE_DIRECTION: '#DD44FF',  // violet      (paper -> future direction)
  RELATED_PROBLEM:  '#FF99DD',  // light pink  (problem <-> related problem)
  // A survey's own structure (backend graph/survey_structure), 2026-09-27:
  // CIE2000 search against the whole palette; closest pair 16.5 dE
  // (REVIEWS_TOOL / CONSOLIDATES), every pair >= 18 CIE76.
  FILED_UNDER:      '#998877',  // warm grey   (item -> survey theme -> survey)
  REVIEWS_TOOL:     '#558822',  // olive green (survey reviews a tool/standard)
  EVOLVED_FROM:     '#9988FF',  // periwinkle  (later -> earlier, as the survey states)
  COMPARED_WITH:    '#AA0000',  // dark red    (a comparison the survey states)
  AFTER_SURVEY:     '#AA5511',  // rust        (a later paper building on reviewed methods)
};

// ---------------------------------------------------------------------------
// Edge labels
// ---------------------------------------------------------------------------
export const EDGE_LABELS = {
  EXTENDS:        'Extends',
  CREATED:        'Created by',
  CO_AUTHORED:    'Co-authored',
  USES_COMPONENT: 'Uses Component',
  APPLIED_TO:     'Applied to',
  CITES:          'Cites',
  REFERENCES:     'References',
  COMPETES_WITH:  'Competes with',
  COMBINES:       'Combines',
  USES_TECHNIQUE: 'Uses Technique',
  DISRUPTS:       'Disrupts',
  CONSOLIDATES:   'Consolidates',
  SOLVES:         'Solves',
  ACHIEVES:       'Achieves',
  FORECAST:       'Forecast (Simpson)',
  EVALUATED_ON:   'Evaluated on',
  MAKES_CLAIM:    'Makes claim',
  SUPPORTS:       'Supports',
  CONTRADICTS:    'Contradicts',
  TECHNIQUE_SYNERGY: 'Synergy (PMI)',
  COVERS:         'Surveys',
  REVIEWS_DATASET:  'Survey reviews dataset',
  REVIEWS_TASK:     'Survey reviews task',
  REPORTED_RESULT:  'As reported by survey',
  FUTURE_DIRECTION: 'Future direction',
  RELATED_PROBLEM:  'Related problem',
  FILED_UNDER:      'Filed under (survey theme)',
  REVIEWS_TOOL:     'Survey reviews tool',
  EVOLVED_FROM:     'Evolved from (per survey)',
  COMPARED_WITH:    'Compared with (per survey)',
  AFTER_SURVEY:     'After the survey, builds on',
};

// ---------------------------------------------------------------------------
// Edge icons (emoji) for detail panel / tooltips
// ---------------------------------------------------------------------------
export const EDGE_TYPE_ICONS = {
  EXTENDS:        '🧬',
  CREATED:        '👤',
  USES_COMPONENT: '⚙️',
  APPLIED_TO:     '🎯',
  CITES:          '📖',
  REFERENCES:     '📄',
  COMPETES_WITH:  '⚔️',
  COMBINES:       '🔗',
  USES_TECHNIQUE: '🛠️',
  DISRUPTS:       '💥',
  CONSOLIDATES:   '🏗️',
  SOLVES:         '✅',
  ACHIEVES:       '🏆',
};
